import { OneOfElements } from '@shared/models/project.model';
import { hasPartialChanges } from '../../object.helpers';
import { ProjectChange, ProjectChangeType } from '../update-engine.models';
import { elementPropertyResolvedToChange } from './element-property-resolved.update';
import { elementPropertyExpressionToChange } from './element-property-expression.update';
import { elementPropertyFormattedToChange } from './element-property-formatted.update';
import { elementPropertyValueToChange } from './element-property-value.update';
import { elementQuantityExpressionToChange } from './element-quantity-expression.update';
import { elementQuantityFallbackExpressionToChange } from './element-quantity-fallback-expression.update';
import {
  elementQuantityFieldToChange,
  mustPersistStaleExpressionQuantityValueOverride,
} from './element-quantity.update';
import { elementQuantityCountToChange } from './element-quantity-count.update';
import { elementQuantitySelectToChange } from './element-quantity-select.update';
import { elementQuantityAutoUpdateToChange } from './element-quantity-auto-update.update';
import { elementCategoryEnrichToChange } from './element-category-enrich.update';
import { elementPropertyFallbackCountToChange } from './element-property-fallback-count.update';
import { elementQuantityFallbackCountToChange } from './element-quantity-fallback-count.update';
import { elementPropertyCountToChange } from './element-property-count.update';
import { elementPropertyRenameToChange } from './element-property-rename.update';
import { elementDataTablePropertiesApplyToChange } from './element-data-table-properties-apply.update';
import { productElementCountResolvedToChange } from './product-element-count-resolved.update';
import { productElementCountExpressionToChange } from './product-element-count-expression.update';
import { productElementCountFormattedToChange } from './product-element-count-formatted.update';
import { productElementProductIdToChange } from './product-element-product-id.update';
import { elementDeactivateToChange } from './element-deactivate.update';
import { elementActivateToChange } from './element-activate.update';
import { elementSoftDeleteToChange } from './element-soft-delete.update';
import {
  elementRecipeApplyToChange,
  elementRecipeClearToChange,
  elementRecipeDetachToChange,
} from './element-recipe.update';
import { elementCategoryToChange } from './element-category.update';
import { productElementEpdToChange } from './product-element-epd.update';
import {
  productElementCarbonLevelOverrideToChange,
  productElementTransportOverrideToChange,
  productElementWasteOverrideToChange,
} from './product-element-granular-override.update';
import { elementPropertyAddToChange } from './element-property-add.update';
import { elementPropertyRemoveToChange } from './element-property-remove.update';
import { elementNameToChange } from './element-name.update';
import { elementOriginToChange } from './element-origin.update';
import { mergeElementPartials } from './merge-element-partials.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Conceptual scope a {@link ProjectChange} targets. Combined with {@link ElementChangeKind}
 * this determines which bucket of {@link BatchChangeMap} the change is routed to and which
 * mutator family applies it.
 *
 * Currently used scopes:
 * - `'element'` — element tree (incl. product elements) on a building version.
 * - `'version'` — building versions themselves (add / remove), their primitives (name /
 *   products / results) and bulk resets.
 * - `'proposal'` — v1 or proposals on a building version.
 * - `'action'` — version actions (proposals-linked).
 *
 * `'building'`, `'project'`, `'design'` are reserved for future change types and are
 * accepted by the type so handler entries can declare them eagerly.
 */
export type ChangeScope =
  | 'project'
  | 'building'
  | 'version'
  | 'element'
  | 'proposal'
  | 'action'
  | 'design';

/**
 * High-level kind for every {@link ProjectChange} processed by the update engine.
 *
 * - `'add'` — insert a new instance under an existing parent (element under parent /
 *   version under building / proposal under version / action under proposal+version).
 *   Always applied via a dedicated mutator.
 * - `'remove'` — drop an existing instance (element subtree / version / proposal / action /
 *   bulk reset of all proposals). Always applied via a dedicated mutator.
 * - `'update'` — in-place patch of an existing instance. For `scope: 'element'` and
 *   primitive `scope: 'version'` updates this is a `Partial<>` produced by a `toPartial`
 *   handler. For all other scopes (`proposal`, `action`, version-scoped structural)
 *   the dispatch goes through a mutator that returns the full updated parent.
 *
 * Application order is fixed across scopes (see {@link BatchChangeMap}) so structural
 * changes always run before partials and proposal/action mutators see freshly added
 * elements but not stale ones.
 */
export type ElementChangeKind = 'add' | 'remove' | 'update';

type ElementPartialFromProjectChangeFn = (
  element: OneOfElements,
  change: ProjectChange,
) => Partial<OneOfElements> | undefined;

/**
 * Registry entry for a {@link ProjectChangeType}. `toPartial` is only set for
 * `scope: 'element'` updates and primitive `scope: 'version'` updates — every other
 * combination is dispatched through a mutator (see `mutators/version-mutate.mutator.ts`).
 */
type ElementChangeHandlerEntry = {
  scope: ChangeScope;
  kind: ElementChangeKind;
  toPartial?: ElementPartialFromProjectChangeFn;
};

const elementChangeHandlers: Partial<
  Record<ProjectChangeType, ElementChangeHandlerEntry>
> = {
  // ── Element scope ─────────────────────────────────────────────────────────
  [ProjectChangeType.ElementAdd]: { scope: 'element', kind: 'add' },
  [ProjectChangeType.ElementRemove]: { scope: 'element', kind: 'remove' },

  [ProjectChangeType.ElementPropertyResolved]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyResolvedToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyExpression]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyExpressionToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyFormatted]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyFormattedToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyValue]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyValueToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityResolved]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityFieldToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityExpression]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityExpressionToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityFallbackExpression]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityFallbackExpressionToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityFormatted]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityFieldToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityValue]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityFieldToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityCount]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityCountToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantitySelect]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantitySelectToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityAutoUpdate]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityAutoUpdateToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementCategoryEnrich]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementCategoryEnrichToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyFallbackCount]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyFallbackCountToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementQuantityFallbackCount]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementQuantityFallbackCountToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyCount]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyCountToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyRename]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyRenameToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementDataTablePropertiesApply]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementDataTablePropertiesApplyToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementDeactivate]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementDeactivateToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementActivate]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementActivateToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementSoftDelete]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementSoftDeleteToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementRecipeApply]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementRecipeApplyToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementRecipeDetach]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementRecipeDetachToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementRecipeClear]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementRecipeClearToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementCategory]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementCategoryToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyAdd]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementPropertyAddToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementPropertyRemove]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      elementPropertyRemoveToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementName]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementNameToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ElementOrigin]: {
    scope: 'element',
    kind: 'update',
    toPartial: elementOriginToChange as ElementPartialFromProjectChangeFn,
  },

  // Product elements live inside the element tree and are dispatched via the same
  // partial pipeline (treated as a sub-kind of element here).
  [ProjectChangeType.ProductElementCountResolved]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementCountResolvedToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementCountExpression]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementCountExpressionToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementCountFormatted]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementCountFormattedToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementProductId]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementProductIdToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementEpd]: {
    scope: 'element',
    kind: 'update',
    toPartial: productElementEpdToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementTransportOverride]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementTransportOverrideToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementWasteOverride]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementWasteOverrideToChange as ElementPartialFromProjectChangeFn,
  },
  [ProjectChangeType.ProductElementCarbonLevelOverride]: {
    scope: 'element',
    kind: 'update',
    toPartial:
      productElementCarbonLevelOverrideToChange as ElementPartialFromProjectChangeFn,
  },

  // ── Version scope ────────────────────────────────────────────────────────
  // Adding/removing a version is structural on the building and goes to its own bucket.
  [ProjectChangeType.VersionAdd]: { scope: 'version', kind: 'add' },
  [ProjectChangeType.VersionRemove]: { scope: 'version', kind: 'remove' },
  // Primitive replacements (toPartial via versionChangeToPartial) and bulk resets
  // (mutator-dispatched via clearProposals). Both go to the version.update bucket;
  // the apply step distinguishes by `toPartial` presence.
  [ProjectChangeType.VersionName]: { scope: 'version', kind: 'update' },
  [ProjectChangeType.VersionProducts]: { scope: 'version', kind: 'update' },
  [ProjectChangeType.VersionResults]: { scope: 'version', kind: 'update' },
  [ProjectChangeType.VersionImports]: { scope: 'version', kind: 'update' },
  [ProjectChangeType.VersionClearProposals]: {
    scope: 'version',
    kind: 'update',
  },

  // ── Proposal scope ───────────────────────────────────────────────────────
  [ProjectChangeType.ProposalAdd]: { scope: 'proposal', kind: 'add' },
  [ProjectChangeType.ProposalDuplicate]: { scope: 'proposal', kind: 'add' },
  [ProjectChangeType.ProposalDelete]: { scope: 'proposal', kind: 'remove' },
  [ProjectChangeType.ProposalUpdate]: { scope: 'proposal', kind: 'update' },
  [ProjectChangeType.ProposalReorder]: { scope: 'proposal', kind: 'update' },
  [ProjectChangeType.ProposalResultsRecord]: {
    scope: 'proposal',
    kind: 'update',
  },

  // ── Action scope ─────────────────────────────────────────────────────────
  [ProjectChangeType.ProposalAppendAction]: { scope: 'action', kind: 'add' },
  [ProjectChangeType.ActionDelete]: { scope: 'action', kind: 'remove' },
  [ProjectChangeType.ActionRename]: { scope: 'action', kind: 'update' },
};

/** Resolve the {@link ElementChangeKind} for a given change type, or `undefined` when unregistered. */
export const getProjectChangeKind = (
  type: ProjectChangeType,
): ElementChangeKind | undefined => elementChangeHandlers[type]?.kind;

/** Resolve the {@link ChangeScope} for a given change type, or `undefined` when unregistered. */
export const getProjectChangeScope = (
  type: ProjectChangeType,
): ChangeScope | undefined => elementChangeHandlers[type]?.scope;

/**
 * Build a partial element patch for a single project change, or undefined if not applicable
 * or if the change would not alter the element.
 *
 * Only returns a partial for `'update'` kind handlers; structural (`'add'`, `'remove'`)
 * changes return `undefined` here and are applied via the structural pipeline instead.
 *
 * No-op filtering is centralized here ({@link hasPartialChanges}) so handlers avoid duplicate
 * deep equality on the hot path; handlers may still return a partial that is equal to the
 * current element (e.g. newly cloned arrays with the same content).
 *
 * @param element - Current element state
 * @param change - Single project change
 * @returns Partial<OneOfElements> or undefined
 */
export const projectChangeToElementPartial = (
  element: OneOfElements,
  change: ProjectChange,
): Partial<OneOfElements> | undefined => {
  const handler = elementChangeHandlers[change.type];
  if (
    !handler ||
    handler.scope !== 'element' ||
    handler.kind !== 'update' ||
    handler.toPartial == null
  ) {
    return undefined;
  }
  // `toPartial` may not have bound yet if this module is reached during a circular import
  // before the per-handler module has finished evaluating (e.g. `element-property.helpers` →
  // validation → `apply-changes-to-proposal` → `pipeline/updates`). Treat as no-op; the
  // handler binds correctly once the cycle resolves.
  const toPartial = handler.toPartial;
  if (!guards.is.function(toPartial)) return undefined;
  const partial = toPartial(element, change);
  if (partial == null) return undefined;
  if (!hasPartialChanges(element, partial)) {
    if (
      change.type === ProjectChangeType.ElementQuantityValue &&
      mustPersistStaleExpressionQuantityValueOverride(element, change)
    ) {
      return partial;
    }
    return undefined;
  }
  return partial;
};

export { mergeElementPartials };

export { elementPropertyResolvedToChange } from './element-property-resolved.update';
export { elementPropertyExpressionToChange } from './element-property-expression.update';
export { elementPropertyFormattedToChange } from './element-property-formatted.update';
export { elementPropertyValueToChange } from './element-property-value.update';
export { elementPropertyCountToChange } from './element-property-count.update';
export { elementQuantityFallbackCountToChange } from './element-quantity-fallback-count.update';
export { elementPropertyRenameToChange } from './element-property-rename.update';
export { elementDataTablePropertiesApplyToChange } from './element-data-table-properties-apply.update';
export { elementQuantityExpressionToChange } from './element-quantity-expression.update';
export { elementQuantityFallbackExpressionToChange } from './element-quantity-fallback-expression.update';
export { elementQuantityFieldToChange } from './element-quantity.update';
export { elementQuantitySelectToChange } from './element-quantity-select.update';
export { productElementCountResolvedToChange } from './product-element-count-resolved.update';
export { productElementCountExpressionToChange } from './product-element-count-expression.update';
export { productElementCountFormattedToChange } from './product-element-count-formatted.update';
export { productElementProductIdToChange } from './product-element-product-id.update';
export { elementDeactivateToChange } from './element-deactivate.update';
export { elementActivateToChange } from './element-activate.update';
export { elementSoftDeleteToChange } from './element-soft-delete.update';
export {
  elementRecipeApplyToChange,
  elementRecipeDetachToChange,
  elementRecipeClearToChange,
} from './element-recipe.update';
export { elementCategoryToChange } from './element-category.update';
export { productElementEpdToChange } from './product-element-epd.update';
export { elementPropertyAddToChange } from './element-property-add.update';
export { elementPropertyRemoveToChange } from './element-property-remove.update';
export { elementNameToChange } from './element-name.update';
export { elementOriginToChange } from './element-origin.update';
